import type { TaskWithRelations } from '@shared/schema';
import jsPDF from 'jspdf';

interface PDFOptions {
  title: string;
  dateRange?:
    | {
        startDate: string;
        endDate: string;
      }
    | undefined;
}

export const generateApprovedTasksPDF = async (
  tasks: TaskWithRelations[],
  options: PDFOptions
): Promise<void> => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 10; // Reduced from 20 to 10
  const contentWidth = pageWidth - 2 * margin;
  let yPosition = margin;

  // Helper function to add a new page if needed
  const checkPageBreak = (requiredHeight: number) => {
    if (yPosition + requiredHeight > pageHeight - margin) {
      doc.addPage();
      yPosition = margin;
    }
  };

  // Title
  doc.setFontSize(16); // Reduced from 20 to 16
  doc.setFont('helvetica', 'bold');
  doc.text(options.title, margin, yPosition);
  yPosition += 12; // Reduced from 15 to 12

  // Date range if provided
  if (options.dateRange) {
    doc.setFontSize(10); // Reduced from 12 to 10
    doc.setFont('helvetica', 'normal');
    const dateText = `Date Range: ${formatDateForPDF(options.dateRange.startDate)} - ${formatDateForPDF(options.dateRange.endDate)}`;
    doc.text(dateText, margin, yPosition);
    yPosition += 8; // Reduced from 10 to 8
  }

  // Summary
  doc.setFontSize(10); // Reduced from 12 to 10
  doc.setFont('helvetica', 'normal');
  doc.text(`Total Approved Tasks: ${tasks.length}`, margin, yPosition);
  yPosition += 12; // Reduced from 15 to 12

  // Table headers
  const tableHeaders = [
    'S.No',
    'Task Title',
    'Assignee',
    'Project',
    'Due Date',
    'Completed',
    'Approved',
  ];
  const columnWidths = [12, 55, 25, 20, 20, 20, 20]; // Increased Task Title width to accommodate description
  const rowHeight = 6; // Reduced from 8 to 6

  // Draw table headers
  doc.setFontSize(8); // Reduced from 10 to 8
  doc.setFont('helvetica', 'bold');
  let xPosition = margin;

  tableHeaders.forEach((header, index) => {
    doc.rect(xPosition, yPosition, columnWidths[index], rowHeight);

    // Wrap header text if needed
    const wrappedHeader = wrapText(header, columnWidths[index] - 3, doc); // Reduced padding from 4 to 3
    let textY = yPosition + 3; // Reduced from 4 to 3

    wrappedHeader.forEach((line, lineIndex) => {
      doc.text(line, xPosition + 1.5, textY); // Reduced padding from 2 to 1.5
      textY += 2.5; // Reduced from 3 to 2.5
    });

    xPosition += columnWidths[index];
  });
  yPosition += rowHeight;

  // Draw table rows
  doc.setFontSize(7); // Reduced from default to 7
  doc.setFont('helvetica', 'normal');
  tasks.forEach((task, index) => {
    checkPageBreak(rowHeight * 3); // Account for task title + description

    xPosition = margin;

    // Prepare task title with description if available
    // Ensure we use the correct font size (7) for width calculations
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');

    const titleMaxWidth = columnWidths[1] - 3; // Task Title column width minus padding
    const wrappedTitle = wrapText(task.title, titleMaxWidth, doc);
    let taskTitleCell = wrappedTitle.join('\n');

    if (task.description && task.description.trim()) {
      // Wrap description separately to ensure it fits
      const descriptionText = `Description: ${task.description}`;
      // Use smaller font (6) for description width calculation since it will be rendered smaller
      doc.setFontSize(6);
      const wrappedDescription = wrapText(descriptionText, titleMaxWidth, doc);
      doc.setFontSize(7); // Reset back
      taskTitleCell = taskTitleCell + '\n' + wrappedDescription.join('\n');
    }

    const rowData = [
      (index + 1).toString(),
      taskTitleCell,
      task.assignee
        ? `${task.assignee.firstName} ${task.assignee.lastName}`
        : 'Unassigned',
      task.project?.name || 'No Project',
      formatDateForPDF(task.dueDate?.toString() || ''),
      formatDateForPDF(task.completedAt?.toString() || ''),
      formatDateForPDF(task.approvedAt?.toString() || ''),
    ];

    // Calculate the maximum height needed for this row
    let maxHeight = rowHeight;
    const cellTexts: string[][] = [];
    const cellLineSpacings: number[][] = []; // Track line spacing for each cell

    rowData.forEach((cell, cellIndex) => {
      // For Task Title column, lines are already wrapped, just use them as-is
      // For other columns, wrap normally
      if (cellIndex === 1 && cell.includes('\n')) {
        // Task Title column - already wrapped, just split by newlines
        const lines = cell.split('\n');
        cellTexts.push(lines);

        // Calculate height based on actual line spacing
        // Title lines use font size 7, description lines use font size 6
        let calculatedHeight = 3; // Top padding
        const lineSpacings: number[] = [];

        lines.forEach((line, lineIdx) => {
          if (line.startsWith('Description:')) {
            // Description line uses font size 6
            calculatedHeight += 2.5; // Spacing for description line
            lineSpacings.push(2.5);
          } else {
            // Title line uses font size 7 - needs slightly more space for readability
            calculatedHeight += 2.8; // Slightly more spacing for title lines
            lineSpacings.push(2.8);
          }
        });

        calculatedHeight += 2; // Bottom padding to prevent overflow

        cellLineSpacings.push(lineSpacings);
        const cellHeight = Math.max(rowHeight, calculatedHeight);
        maxHeight = Math.max(maxHeight, cellHeight);
      } else {
        // Other columns - wrap normally
        const wrappedText = wrapText(cell, columnWidths[cellIndex] - 3, doc);
        cellTexts.push(wrappedText);
        const cellHeight = Math.max(
          rowHeight,
          3 + wrappedText.length * 2.5 + 2
        ); // 3 for top padding, 2 for bottom
        maxHeight = Math.max(maxHeight, cellHeight);
        cellLineSpacings.push(wrappedText.map(() => 2.5)); // Default spacing for other columns
      }
    });

    // Ensure minimum height - at least 2x rowHeight if there are descriptions
    const hasDescriptions =
      rowData[1]?.includes('\n') && rowData[1]?.includes('Description:');
    if (hasDescriptions) {
      maxHeight = Math.max(maxHeight, rowHeight * 2.5);
    }

    // Draw the row with proper height
    rowData.forEach((cell, cellIndex) => {
      doc.rect(xPosition, yPosition, columnWidths[cellIndex], maxHeight);

      const wrappedText = cellTexts[cellIndex];
      const lineSpacings = cellLineSpacings[cellIndex] || [];
      let textY = yPosition + 3; // Top padding

      wrappedText.forEach((line, lineIndex) => {
        // Use smaller italic font for description line (lines after title in Task Title column)
        let currentFontSize = 7;
        const lineSpacing = lineSpacings[lineIndex] || 2.5;

        if (cellIndex === 1 && cell.includes('\n')) {
          // Check if this line starts with "Description:"
          if (line.startsWith('Description:')) {
            currentFontSize = 6;
            doc.setFontSize(6);
            doc.setFont('helvetica', 'italic');
          } else {
            currentFontSize = 7;
            doc.setFontSize(7);
            doc.setFont('helvetica', 'normal');
          }
        } else {
          currentFontSize = 7;
          doc.setFontSize(7);
          doc.setFont('helvetica', 'normal');
        }

        // Ensure text doesn't exceed cell width using the current font size
        // Be more conservative with padding to prevent overflow
        const maxCellWidth = columnWidths[cellIndex] - 4; // Increased padding to prevent overflow
        let displayLine = line;

        // Measure with current font settings
        let textWidth = doc.getTextWidth(displayLine);

        // If too wide, truncate aggressively
        if (textWidth > maxCellWidth) {
          // Calculate max characters more conservatively
          const charWidth = doc.getTextWidth('M'); // Use 'M' as it's wider than 'A'
          const maxChars = Math.floor((maxCellWidth * 0.95) / charWidth); // 95% to be safe

          // Truncate character by character until it fits
          while (textWidth > maxCellWidth && displayLine.length > 0) {
            if (displayLine.length <= 3) {
              displayLine = '...';
              break;
            }
            displayLine = displayLine.substring(0, displayLine.length - 1);
            textWidth = doc.getTextWidth(displayLine + '...');
          }

          if (displayLine.length > 0 && !displayLine.endsWith('...')) {
            displayLine = displayLine + '...';
          }

          // Final safety check
          const finalWidth = doc.getTextWidth(displayLine);
          if (finalWidth > maxCellWidth) {
            // Emergency truncation - remove one more character
            while (
              doc.getTextWidth(displayLine) > maxCellWidth &&
              displayLine.length > 3
            ) {
              displayLine =
                displayLine.substring(0, displayLine.length - 4) + '...';
            }
          }
        }

        doc.text(displayLine, xPosition + 1.5, textY); // Reduced padding from 2 to 1.5
        textY += lineSpacing; // Use calculated line spacing for this line
      });

      xPosition += columnWidths[cellIndex];
    });

    // Reset font
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    yPosition += maxHeight;

    // Add some spacing between rows
    yPosition += 1;
  });

  // Footer
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(6); // Reduced from 8 to 6
    doc.setFont('helvetica', 'normal');
    doc.text(
      `Generated on ${new Date().toLocaleDateString()} - Page ${i} of ${totalPages}`,
      margin,
      pageHeight - 8 // Reduced from 10 to 8
    );
  }

  // Save the PDF
  const fileName = `approved-tasks-${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
};

const formatDateForPDF = (dateString: string): string => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return 'N/A';
  }
};

const truncateText = (text: string, maxLength: number): string => {
  if (!text) return '';
  return text.length > maxLength
    ? text.substring(0, maxLength - 3) + '...'
    : text;
};

const wrapText = (text: string, maxWidth: number, doc: jsPDF): string[] => {
  if (!text) return [''];

  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = '';

  words.forEach(word => {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const testWidth = doc.getTextWidth(testLine);

    if (testWidth <= maxWidth) {
      currentLine = testLine;
    } else {
      if (currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        // Word is too long, split it
        const charWidth = doc.getTextWidth('A');
        const maxChars = Math.floor(maxWidth / charWidth);
        if (word.length > maxChars) {
          lines.push(word.substring(0, maxChars - 3) + '...');
          currentLine = '';
        } else {
          currentLine = word;
        }
      }
    }
  });

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines.length > 0 ? lines : [''];
};

export const filterTasksByDateRange = (
  tasks: TaskWithRelations[],
  startDate: string,
  endDate: string
): TaskWithRelations[] => {
  if (!startDate || !endDate) return tasks;

  const start = new Date(startDate);
  const end = new Date(endDate);
  end.setHours(23, 59, 59, 999); // Include the entire end date

  return tasks.filter(task => {
    if (!task.approvedAt) return false;
    const approvedDate = new Date(task.approvedAt);
    return approvedDate >= start && approvedDate <= end;
  });
};

export const generateProjectTasksPDF = async (
  tasks: TaskWithRelations[],
  options: { title: string; projectName?: string; filterType?: string }
): Promise<void> => {
  if (tasks.length === 0) {
    throw new Error('No tasks to generate PDF');
  }

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 10;
  let yPosition = margin;

  // Helper function to add a new page if needed
  const checkPageBreak = (requiredHeight: number) => {
    if (yPosition + requiredHeight > pageHeight - margin) {
      doc.addPage();
      yPosition = margin;
    }
  };

  // Title
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(options.title, margin, yPosition);
  yPosition += 12;

  // Project name if provided
  if (options.projectName) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Project: ${options.projectName}`, margin, yPosition);
    yPosition += 8;
  }

  // Summary
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Total Tasks: ${tasks.length}`, margin, yPosition);
  yPosition += 8;

  // Filter type if provided
  if (options.filterType) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'italic');
    doc.text(`Filter: ${options.filterType}`, margin, yPosition);
    yPosition += 8;
  }

  yPosition += 5;

  // Table headers
  const tableHeaders = [
    'S.No',
    'Task Title',
    'Assignee',
    'Status',
    'Priority',
    'Due Date',
    'Created',
  ];
  const columnWidths = [12, 50, 30, 25, 18, 25, 25];
  const rowHeight = 7;

  // Draw table headers
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  let xPosition = margin;

  tableHeaders.forEach((header, index) => {
    doc.rect(xPosition, yPosition, columnWidths[index], rowHeight);
    const wrappedHeader = wrapText(header, columnWidths[index] - 3, doc);
    let textY = yPosition + 4;
    wrappedHeader.forEach(line => {
      doc.text(line, xPosition + 1.5, textY);
      textY += 2.5;
    });
    xPosition += columnWidths[index];
  });
  yPosition += rowHeight;

  // Draw table rows
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  tasks.forEach((task, index) => {
    checkPageBreak(rowHeight * 2);

    xPosition = margin;
    const rowData = [
      (index + 1).toString(),
      task.title || 'Untitled',
      task.assignee
        ? `${task.assignee.firstName} ${task.assignee.lastName}`
        : 'Unassigned',
      task.status.replace('_', ' ').toUpperCase(),
      `#${(task.priority || 0) + 1}`,
      formatDateForPDF(task.dueDate?.toString() || ''),
      formatDateForPDF(task.createdAt?.toString() || ''),
    ];

    // Calculate the maximum height needed for this row
    let maxHeight = rowHeight;
    const cellTexts: string[][] = [];

    rowData.forEach((cell, cellIndex) => {
      const wrappedText = wrapText(cell, columnWidths[cellIndex] - 3, doc);
      cellTexts.push(wrappedText);
      const cellHeight = Math.max(rowHeight, wrappedText.length * 2.5);
      maxHeight = Math.max(maxHeight, cellHeight);
    });

    // Draw the row with proper height
    rowData.forEach((cell, cellIndex) => {
      doc.rect(xPosition, yPosition, columnWidths[cellIndex], maxHeight);

      const wrappedText = cellTexts[cellIndex];
      let textY = yPosition + 4;

      wrappedText.forEach(line => {
        doc.text(line, xPosition + 1.5, textY);
        textY += 2.5;
      });

      xPosition += columnWidths[cellIndex];
    });

    yPosition += maxHeight;

    // Add task description if available
    if (task.description && task.description.trim()) {
      checkPageBreak(12);
      yPosition += 1;
      doc.setFontSize(6);
      doc.setFont('helvetica', 'italic');
      const description = truncateText(task.description, 150);
      yPosition += 2;
      doc.text(`Description: ${description}`, margin, yPosition);
      yPosition += 6;
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
    }
  });

  // Footer
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    doc.text(
      `Generated on ${new Date().toLocaleDateString()} - Page ${i} of ${totalPages}`,
      margin,
      pageHeight - 8
    );
  }

  // Save the PDF
  const filterSuffix = options.filterType
    ? `-${options.filterType.toLowerCase().replace(/\s+/g, '-')}`
    : '';
  const fileName = `project-tasks${options.projectName ? `-${options.projectName}` : ''}${filterSuffix}-${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
};

interface ProjectWithStats {
  id: string;
  name: string;
  description?: string | null;
  color?: string | null;
  createdAt?: Date | string | null;
  totalTasks: number;
  completedTasks: number;
  inProgressTasks: number;
  pendingTasks: number;
  notCompletedTasks: number;
  completionRate: number;
}

export const generateProjectsPDF = async (
  projects: ProjectWithStats[],
  options: { filterType?: string }
): Promise<void> => {
  if (projects.length === 0) {
    throw new Error('No projects to generate PDF');
  }

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 10;
  let yPosition = margin;

  // Helper function to add a new page if needed
  const checkPageBreak = (requiredHeight: number) => {
    if (yPosition + requiredHeight > pageHeight - margin) {
      doc.addPage();
      yPosition = margin;
    }
  };

  // Title
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('Projects Report', margin, yPosition);
  yPosition += 12;

  // Filter type if provided
  if (options.filterType) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Filter: ${options.filterType}`, margin, yPosition);
    yPosition += 8;
  }

  // Summary
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Total Projects: ${projects.length}`, margin, yPosition);
  yPosition += 8;

  const totalTasks = projects.reduce((sum, p) => sum + p.totalTasks, 0);
  const totalCompleted = projects.reduce((sum, p) => sum + p.completedTasks, 0);
  const totalInProgress = projects.reduce(
    (sum, p) => sum + p.inProgressTasks,
    0
  );

  doc.text(`Total Tasks Across All Projects: ${totalTasks}`, margin, yPosition);
  yPosition += 8;
  doc.text(`Total Completed: ${totalCompleted}`, margin, yPosition);
  yPosition += 8;
  doc.text(`Total In Progress: ${totalInProgress}`, margin, yPosition);
  yPosition += 12;

  // Table headers
  const tableHeaders = [
    'S.No',
    'Project Name',
    'Total',
    'Completed',
    'In Progress',
    'Not Completed',
    'Progress %',
  ];
  const columnWidths = [12, 50, 20, 22, 22, 28, 22];
  const rowHeight = 8;

  // Draw table headers
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  let xPosition = margin;

  tableHeaders.forEach((header, index) => {
    doc.rect(xPosition, yPosition, columnWidths[index], rowHeight);
    const wrappedHeader = wrapText(header, columnWidths[index] - 3, doc);
    let textY = yPosition + 4;
    wrappedHeader.forEach(line => {
      doc.text(line, xPosition + 1.5, textY);
      textY += 2.5;
    });
    xPosition += columnWidths[index];
  });
  yPosition += rowHeight;

  // Draw table rows
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  projects.forEach((project, index) => {
    checkPageBreak(rowHeight * 3); // Account for project name + created date

    xPosition = margin;

    // Prepare project name with creation date
    let projectNameCell = project.name || 'Untitled';
    if (project.createdAt) {
      const createdDate = formatDateForPDF(project.createdAt.toString());
      projectNameCell = `${project.name || 'Untitled'}\nCreated: ${createdDate}`;
    }

    const rowData = [
      (index + 1).toString(),
      projectNameCell,
      project.totalTasks.toString(),
      project.completedTasks.toString(),
      project.inProgressTasks.toString(),
      project.notCompletedTasks.toString(),
      `${project.completionRate}%`,
    ];

    // Calculate the maximum height needed for this row
    let maxHeight = rowHeight;
    const cellTexts: string[][] = [];

    rowData.forEach((cell, cellIndex) => {
      // Split by newlines first, then wrap each line
      const lines = cell.split('\n');
      const allWrappedLines: string[] = [];
      lines.forEach(line => {
        const wrappedText = wrapText(line, columnWidths[cellIndex] - 3, doc);
        allWrappedLines.push(...wrappedText);
      });
      cellTexts.push(allWrappedLines);
      const cellHeight = Math.max(rowHeight, allWrappedLines.length * 2.5);
      maxHeight = Math.max(maxHeight, cellHeight);
    });

    // Draw the row with proper height
    rowData.forEach((cell, cellIndex) => {
      doc.rect(xPosition, yPosition, columnWidths[cellIndex], maxHeight);

      const wrappedText = cellTexts[cellIndex];
      let textY = yPosition + 4;

      wrappedText.forEach((line, lineIndex) => {
        // Use smaller font for creation date (lines after the first)
        if (cellIndex === 1 && lineIndex > 0 && cell.includes('\n')) {
          doc.setFontSize(6);
        } else {
          doc.setFontSize(7);
        }
        doc.text(line, xPosition + 1.5, textY);
        textY += 2.5;
      });

      xPosition += columnWidths[cellIndex];
    });

    // Reset font size
    doc.setFontSize(7);
    yPosition += maxHeight;

    // Add project description if available (outside the table cell)
    if (project.description && project.description.trim()) {
      checkPageBreak(12);
      yPosition += 2;
      doc.setFontSize(6);
      doc.setFont('helvetica', 'italic');
      const description = truncateText(project.description, 150);
      doc.text(`Description: ${description}`, margin, yPosition);
      yPosition += 6;
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
    }

    // Add some spacing between rows
    yPosition += 2;
  });

  // Footer
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    doc.text(
      `Generated on ${new Date().toLocaleDateString()} - Page ${i} of ${totalPages}`,
      margin,
      pageHeight - 8
    );
  }

  // Save the PDF
  const filterSuffix = options.filterType
    ? `-${options.filterType.toLowerCase().replace(/\s+/g, '-')}`
    : '';
  const fileName = `projects${filterSuffix}-${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
};
